import { Injectable, inject, signal } from '@angular/core';
import {
  Document,
  DocumentAttributeType,
  DocumentType,
  InProgressDocument,
  isDocument,
  isInProgressDocument,
  RequiredDocumentType,
} from '@documents/models/document-type';
import {
  selectEntityDocuments,
  selectUploadingEntityDocuments,
} from '@documents/selectors/documents.selectors';
import { DocumentsService } from '@documents/services/documents.service';
import { Store } from '@ngrx/store';
import { DocumentInfo } from '@shared/interfaces/document.interface';
import { cloneDeep, uniqueId } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  Observable,
  take,
} from 'rxjs';

import { QuestionnaireService } from './questionnaire.service';
import { SubmissionsService } from './submissions.service';
import {
  createCompositeKey,
  getAllQuestionnaireQuestions,
} from '../questionnaire-utils';
import { OutcomesService } from './outcomes.service';
import { PermissionsService } from '@shared/services/permissions.service';
import {
  PermissionGrants,
  SiteSubmission,
  StudySubmission,
} from '@shared/interfaces/permissions.interface';
import { Submission } from '../models/submission.model';
import { selectQuestionnaire } from '../redux/questionnaire.selectors';
import { Questionnaire } from '../models/questionnaire.model';
import { UPLOAD_DIALOG_ACTIONS } from '@documents/actions';
import { UploadDialogData } from '@shared/dialogs/types/upload-document-data.types';
import { WcgUploadDocumentComponent } from '@data-access/submissions/dialogs/components/upload-document/upload-document.component';
import { WcgUploadRequestedDocumentComponent } from '@data-access/submissions/dialogs/components/upload-requested-document/upload-requested-document.component';
import { saveAs, sanitizeFilename } from '@shared/utils/download.util';

/**
 * @deprecated - This interface is no longer used. Use individual signals instead.
 */
export interface DocumentView {
  documents: Array<Document | InProgressDocument>;
  documentLoadError: boolean;
  documentTypes: DocumentType[];
  attributeTypes: DocumentAttributeType[];
  requiredDocumentTypes: RequiredDocumentType[];
  activeDocument?: Document;
  notificationsDocumentsCount: number;
  canEdit: boolean;
  canRemove: boolean;
}

@Injectable()
export class DocumentViewFacade {
  private readonly documentService = inject(DocumentsService);
  private readonly store = inject(Store);
  private readonly submissionService = inject(SubmissionsService);
  private readonly questionnaireService = inject(QuestionnaireService);
  private readonly outcomeService = inject(OutcomesService);
  private readonly permissionsService = inject(PermissionsService);

  // Signals for document view state
  $documents = signal<Array<Document | InProgressDocument>>([]);
  $documentLoadError = signal<boolean>(false);
  $documentTypes = signal<DocumentType[]>([]);
  $attributeTypes = signal<DocumentAttributeType[]>([]);
  $requiredDocumentTypes = signal<RequiredDocumentType[]>([]);
  $activeDocument = signal<Document | undefined>(undefined);
  $notificationsDocumentsCount = signal<number>(0);
  $canEdit = signal<boolean>(false);
  $canRemove = signal<boolean>(false);
  $selectedDocumentFromCollabhub = signal<boolean>(false);

  constructor() {
    // Initialize signals from observables
    this._customListeners();
  }

  private _customListeners(): void {
    // Subscribe to the DocumentsService activeDocument$ and update our signal
    this.documentService.activeDocument$?.subscribe((doc) => {
      this.$activeDocument.set(doc);
    });

    combineLatest([
      this.store.select(selectQuestionnaire).pipe(
        distinctUntilChanged(),
        filter(
          (questionnaire): questionnaire is Questionnaire =>
            questionnaire !== undefined,
        ),
      ),
      this.submissionService.submission$?.pipe(
        distinctUntilChanged(
          (a: Submission, b: Submission) =>
            a.id === b.id || a.current_stage !== b.current_stage,
        ),
      ),
    ]).subscribe(([questionnaire, submission]) => {
      // Handle permissions
      this.permissionsService
        .observeEntityPermissions(
          submission!.id,
          [
            submission!.entity_type === 'site'
              ? SiteSubmission
              : StudySubmission,
          ],
          [PermissionGrants.CanEdit],
        )
        .subscribe((canEditValue) => {
          const grantedPermissions = submission.role?.permissions?.some((p) => {
            const editable = 'can_edit' in p ? p.can_edit : p.canEdit;
            return editable;
          });
          this.$canEdit.set(canEditValue || grantedPermissions || false);
        });

      this.permissionsService
        .observeEntityPermissions(
          submission!.id,
          [
            submission!.entity_type === 'site'
              ? SiteSubmission
              : StudySubmission,
          ],
          [PermissionGrants.CanRemove],
        )
        .subscribe((canRemoveValue) => {
          const grantedPermissions = submission.role?.permissions?.some((p) => {
            const canRemove = 'can_remove' in p ? p.can_remove : p.canRemove;
            const canEdit = 'can_edit' in p ? p.can_edit : p.canEdit;
            return canEdit || canRemove;
          });
          this.$canRemove.set(canRemoveValue || grantedPermissions || false);
        });

      // Document types
      this.documentService
        .getDocumentTypes(submission!.type?.id, submission!.entity_type)
        .subscribe((types) => {
          this.$documentTypes.set(types);
        });

      // Attribute types
      this.documentService
        .getAttributeTypes(submission!.entity_type)
        .subscribe((types) => {
          this.$attributeTypes.set(types);
        });

      // Required document types
      combineLatest({
        allDocumentTypes: this.documentService.getDocumentTypes(
          submission.type_id,
        ),
        state: this.questionnaireService.getQuestionnaireState(submission.id),
      }).subscribe(({ state, allDocumentTypes }) => {
        const allQuestions = getAllQuestionnaireQuestions(questionnaire);

        const visibleStateDocumentQuestionIds: string[] = Object.values(
          state.questions,
        )
          .filter((q) => q.type === 'document' && q.visible)
          .map((q) => ('id' in q && q.id) as string);

        const visibleDocumentQuestions = allQuestions.filter((q) =>
          visibleStateDocumentQuestionIds.includes(q.id),
        );

        const requiredDocumentTypes = visibleDocumentQuestions
          .map((question) => {
            // Turn IDs into document types by looking them up in the allDocumentTypes array
            const documentType = allDocumentTypes.find(
              (dt) => dt.id === question.data.documentTypeId,
            );
            if (documentType) {
              const updatedDocumentType = cloneDeep(documentType);

              // Compute a title
              updatedDocumentType.title =
                question.data?.documentName?.trim() ?? documentType.name;

              // Overwrite the name with the title
              updatedDocumentType.name = `${documentType.name}`;

              // Save a question ID for later
              updatedDocumentType.questionId = question.id;
              updatedDocumentType.answerId = question?.answer?.id;

              return updatedDocumentType;
            }
            return documentType;
          })
          // Filter out any undefined document types
          .filter(
            (documentType): documentType is DocumentType => !!documentType,
          )
          // Transform the document types into required document types
          .map(
            (documentType: DocumentType): RequiredDocumentType => ({
              id: documentType.questionId ?? documentType.id,
              name: documentType.name,
              document_name: documentType.title, // feeds the document name
              optional: false,
              document_type: documentType,
              workspace_type: submission!.type,
              created_dt: documentType.created_dt,
              updated_dt: documentType.updated_dt,
              questionId: documentType.questionId,
              answerId: documentType.answerId,
            }),
          );

        this.$requiredDocumentTypes.set(requiredDocumentTypes);
      });

      // Documents
      combineLatest({
        documents: this.documentService.getDocuments(
          submission!.id,
          'workspace',
        ),
        state: this.questionnaireService.getQuestionnaireState(submission.id),
      }).subscribe(({ state, documents }) => {
        const allQuestions = getAllQuestionnaireQuestions(questionnaire);

        const visibleStateDocumentQuestionIds = Object.values(state.questions)
          .filter(
            (q) =>
              ('type' in q && q.type) === 'document' &&
              'visible' in q &&
              q.visible,
          )
          .map((q) => 'id' in q && q.id);

        const visibleDocumentQuestions = allQuestions.filter((q) =>
          visibleStateDocumentQuestionIds.includes(q.id),
        );

        const requiredDocumentTypeNames = visibleDocumentQuestions
          .filter(
            // Select only the document questions that are visible
            (question) => {
              const compKey = createCompositeKey(question);

              return (
                state.questions[compKey]?.visible &&
                question.type.name === 'document'
              );
            },
          )
          .map((question) => question.data?.documentName?.trim());

        // Map through existing documents and set 'required' based on the questionnaire
        const processedDocuments = documents.map(
          (doc: Document | InProgressDocument) => {
            const returnValue: Document | InProgressDocument = {
              ...doc,
            };

            if (isDocument(doc)) {
              returnValue.required = requiredDocumentTypeNames.includes(
                doc?.name,
              );
              returnValue.is_additional = !returnValue.required;
            } else if (isInProgressDocument(doc)) {
              const required = requiredDocumentTypeNames.includes(doc?.name);

              returnValue.required = required;
              returnValue.is_additional = !required;
            }

            return returnValue;
          },
        );

        this.$documents.set(processedDocuments);
      });

      // Document load error
      this.documentService
        .getDocumentsError(submission!.id, 'workspace')
        .subscribe((error) => {
          this.$documentLoadError.set(error);
        });

      // Notifications documents count
      this.submissionService.notificationsDocumentsCount$.subscribe((count) => {
        this.$notificationsDocumentsCount.set(count);
      });
    });
  }

  setActiveDocument(document: any) {
    this.$activeDocument.set(document);
    return this.documentService.setActiveDocument(document);
  }

  markCompleteSubmissionDocument(document: {
    documentId: string;
    entityId: string;
    entityType: string;
    complete: boolean;
  }) {
    this.documentService
      .updateDocumentCompletion(
        document.documentId,
        document.entityId,
        document.entityType,
        document.complete,
      )
      .subscribe(() => this.submissionService.updateSubmissionStatus());
  }

  requestDocument(request: {
    text: string;
    document_type_id: string;
    workspace_id: string;
  }) {
    this.documentService.requestDocument(request);
  }

  selectDocument(documentId: string, submissionId: string) {
    combineLatest([
      this.store.select(selectEntityDocuments(submissionId, 'workspace')),
      this.store.select(selectUploadingEntityDocuments(submissionId)),
      this.outcomeService.outcomes$,
    ])
      .pipe(take(1))
      .subscribe(([documents, inProgressDoc, outcome]) => {
        if (documents) {
          documents.forEach((doc) => {
            if (doc.id === documentId) {
              this.setActiveDocument(doc);
              this.$selectedDocumentFromCollabhub.set(true);
            }
          });
        }
        if (outcome) {
          outcome.forEach((doc: any) => {
            if (doc.id === documentId) {
              this.setActiveDocument(doc);
              this.$selectedDocumentFromCollabhub.set(true);
            }
          });
        }
        inProgressDoc.forEach((doc) => {
          if (doc.document?.id === documentId) {
            this.setActiveDocument(doc.document);
            this.$selectedDocumentFromCollabhub.set(true);
          }
        });
      });
  }

  downloadDocument(documentData: { id: string; title: string }) {
    this.documentService.downloadDocument(documentData.id).subscribe({
      next: ({ blob, filename }) => {
        saveAs(blob, filename);
      },
      error: (error: any) => {
        console.error('Error downloading the document:', error);
      },
    });
  }

  uploadDocument(
    submissionId: string,
    file: File,
    documentTypeId: string,
    documentTypeName: string,
    documentName: string,
    additional?: boolean,
    questionId?: string,
    isResponse?: boolean,
    is_reviewer_document = false,
  ) {
    const documentId = uniqueId();

    return this.documentService.uploadDocument(
      documentId,
      file,
      documentTypeId,
      documentTypeName,
      documentName,
      submissionId,
      'workspace',
      additional,
      questionId,
      isResponse,
      is_reviewer_document,
    );

    // TODO: We should format the documents
    // array in the view to include the pending uploads along w/ their progress
  }

  uploadRequestedDocument(data: {
    file: File;
    documentTypeId: string;
    documentTypeName: string;
    documentName: string;
    threadId: string;
    msgEntityId: string;
  }) {
    this.documentService.uploadRequestedDocument(
      data.file,
      data.documentTypeId,
      data.documentTypeName,
      data.documentName,
      data.threadId,
      data.msgEntityId,
    );
  }

  cancelUploadDocument(submissionId: string, cancelDocumentId: string) {
    this.documentService.cancelUpload(submissionId, cancelDocumentId);
  }

  updateDocument(document: Document) {
    this.documentService.updateDocument(document);
  }

  deleteDocument(documentInfo: DocumentInfo) {
    this.documentService.deleteDocument(
      documentInfo.id,
      documentInfo.entityId,
      documentInfo.entityType,
    );
  }

  uploadDocuments(
    submissionId: string,
    documents: {
      file: File;
      documentTypeId: string;
      documentTypeName: string;
      documentName: string;
      isAdditional: boolean;
      questionId?: string;
      isResponse?: boolean;
      is_reviewer_document?: boolean;
    }[],
  ): Observable<unknown> {
    return forkJoin(
      documents.map((document) =>
        this.uploadDocument(
          submissionId,
          document.file,
          document.documentTypeId,
          document.documentTypeName,
          document.documentName,
          document.isAdditional,
          document.questionId,
          document.isResponse,
          document.is_reviewer_document,
        ),
      ),
    );
  }

  getSubmissionDocuments(submissionId: string) {
    return this.documentService.getDocuments(submissionId, 'workspace');
  }

  /**
   * Load the document types for a submission.
   * @param typeId - The type ID of the submission.
   * @param entityType - The entity type of the submission.
   */
  loadDocumentTypes(typeId?: string, entityType?: string) {
    this.documentService
      .getDocumentTypes(typeId, entityType)
      .pipe(take(1))
      .subscribe();
  }

  removeDocuments(
    submissionId: string,
    documentIds: string[],
    removeAllDocuments?: boolean,
  ) {
    this.documentService.removeDocuments(
      submissionId,
      'workspace',
      documentIds,
      removeAllDocuments,
    );
  }

  getDocumentThumbnail(documentId: string) {
    return this.documentService.getDocumentThumbnail(documentId);
  }

  openUploadDialog(data: UploadDialogData) {
    this.store.dispatch(
      UPLOAD_DIALOG_ACTIONS.openUploadDialog({
        component: () => WcgUploadDocumentComponent,
        data,
      }),
    );
  }

  openRequestedUploadDialog(data: UploadDialogData) {
    this.store.dispatch(
      UPLOAD_DIALOG_ACTIONS.openRequestedUploadDialog({
        component: () => WcgUploadRequestedDocumentComponent,
        data,
      }),
    );
  }

  /**
   * Validates the filename for forbidden characters and length.
   * @param filename The filename to validate.
   * @return An object with validation errors or null if valid.
   * @example
   * const errors = this.validateFilename('invalid/file:name.txt');
   * if (errors) {
   *  console.error(errors);
   * }
   * @returns {Record<string, true> | null} An object with validation errors or null if valid.
   */
  validateFilename(filename: string): Record<string, true> | null {
    const noTrailingPeriods = (value?: string) => {
      if (!value) {
        return false;
      }

      return value.endsWith('.');
    };

    const hasInvalidCharacters = (value?: string) => {
      if (!value) {
        return false;
      }
      const sanitized = sanitizeFilename(value);
      return value !== sanitized;
    };

    const validNameLength = (value?: string) => {
      if (!value) {
        return false;
      }

      // between 0 and 255 characters
      return value.length === 0 || value.length > 255;
    };

    if (noTrailingPeriods(filename)) {
      return { trailingPeriod: true };
    }

    if (hasInvalidCharacters(filename)) {
      return { forbiddenCharacters: true };
    }

    if (validNameLength(filename)) {
      return { invalidName: true };
    }

    return null;
  }
}
